import React, { useEffect, useState } from "react";
import { Form, Input, Select, Button, Card, message, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./Franchise.module.css";
import Cookies from "js-cookie";
import { BASE_URL } from "../../Apis/BaseUrl";
import axios from "axios";
import TopNavbar from "../../Component/TopMenu/TopNavbar";
import NavbarSection from "../../Component/NavbarSection/NavbarSection";
import { Helmet } from "react-helmet";

const EditFranchise = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams(); // Get franchise ID from URL
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true); // For initial data load
  const [sellers, setSellers] = useState([]);
  const [franchiseName, setFranchiseName] = useState("");

  // Fetch all sellers for the dropdown
  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const token = Cookies.get("v&b_token")
          ? JSON.parse(Cookies.get("v&b_token"))?.token
          : null;
        const res = await axios.get(`${BASE_URL}/franchise/sellers`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setSellers(res.data);
      } catch (error) {
        message.error("Failed to load sellers.");
        console.error("Error fetching sellers:", error);
      }
    };
    fetchSellers();
  }, []);

  // Fetch franchise data for editing
  useEffect(() => {
    const fetchFranchiseData = async () => {
      setIsFetching(true);
      try {
        const token = Cookies.get("v&b_token")
          ? JSON.parse(Cookies.get("v&b_token"))?.token
          : null;
        if (!token) {
          throw new Error("Admin token not found");
        }
        const response = await axios.get(`${BASE_URL}/franchise/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const franchiseData = response.data;
        setFranchiseName(franchiseData.name);
        // Pre-populate form fields
        form.setFieldsValue({
          name: franchiseData.name,
          email: franchiseData.email,
          // Do not pre-fill password for security/UX reasons
          sellers: franchiseData.sellers.map((s) => s.value), // Set selected seller IDs
        });
      } catch (err) {
        message.error(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch franchise data"
        );
        navigate("/franchise"); // Redirect if data fetch fails
      } finally {
        setIsFetching(false);
      }
    };

    if (id) {
      fetchFranchiseData();
    }
  }, [id, form, navigate]);

  const onFinish = async (values) => {
    console.log("Update values:", values);
    setIsLoading(true);
    try {
      const token = Cookies.get("v&b_token")
        ? JSON.parse(Cookies.get("v&b_token"))?.token
        : null;
      if (!token) {
        throw new Error("Admin token not found");
      }

      // Filter out empty password field if not changed
      const updatePayload = { ...values };
      if (!updatePayload.password || updatePayload.password.trim() === "") {
        delete updatePayload.password;
      }

      const response = await axios.put(
        `${BASE_URL}/franchise/${id}`,
        updatePayload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        message.success("Franchise updated successfully!");
        navigate("/franchise");
      } else {
        message.error(
          response.data.message || "Update failed. Please try again."
        );
      }
    } catch (error) {
      message.error(
        error.response?.data?.message ||
          error.message ||
          "An error occurred during update. Please try again later."
      );
      console.error("Error updating franchise:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div>
        <Helmet>
          <title>Edit Franchise</title>
        </Helmet>
        <TopNavbar />
        <NavbarSection />
        <div style={{ padding: "40px", textAlign: "center" }}>
          <Spin tip="Loading franchise data..." size="large">
            <div style={{ minHeight: "300px" }} />
          </Spin>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Edit Franchise</title>
      </Helmet>
      <TopNavbar />
      <NavbarSection />

      {/* Hero Section Container */}
      <div style={{ padding: "30px 40px" }}>
        <div style={{ marginBottom: "30px" }}>
          <h6
            style={{
              color: "#AE0000",
              fontSize: "1.3rem",
              fontWeight: "600",
              margin: 0,
            }}
          >
            Edit Franchise: {franchiseName}
          </h6>
        </div>

        {/* Form Container */}
        <div
          style={{
            border: "1px solid #E0E1E0",
            borderRadius: "6px",
            padding: "30px",
            backgroundColor: "white",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item
              name="name"
              label={
                <span style={{ fontWeight: "500", color: "#333" }}>Name</span>
              }
              rules={[{ required: true, message: "Please input the name!" }]}
            >
              <Input
                placeholder="Enter franchise name"
                autoComplete="off"
                style={{
                  height: "40px",
                  borderRadius: "6px",
                  border: "1px solid #E0E1E0",
                }}
              />
            </Form.Item>

            <Form.Item
              name="email"
              label={
                <span style={{ fontWeight: "500", color: "#333" }}>Email</span>
              }
              rules={[
                { required: true, message: "Please input the email!" },
                { type: "email", message: "Please enter a valid email!" },
              ]}
            >
              <Input
                placeholder="Enter franchise email"
                autoComplete="off"
                style={{
                  height: "40px",
                  borderRadius: "6px",
                  border: "1px solid #E0E1E0",
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={
                <span style={{ fontWeight: "500", color: "#333" }}>
                  Password (leave blank to keep unchanged)
                </span>
              }
              // Password is not required for update
              rules={[
                { min: 6, message: "Password must be at least 6 characters" },
              ]}
            >
              <Input.Password
                placeholder="Enter new password or leave blank"
                autoComplete="new-password"
                style={{
                  height: "40px",
                  borderRadius: "6px",
                  border: "1px solid #E0E1E0",
                }}
              />
            </Form.Item>

            <Form.Item
              name="sellers"
              label={
                <span style={{ fontWeight: "500", color: "#333" }}>
                  Select Sellers
                </span>
              }
              rules={[
                {
                  required: true,
                  message: "Please select at least one seller!",
                },
              ]}
            >
              <Select
                mode="multiple"
                placeholder="Select sellers"
                options={sellers} // Already fetched and formatted
                optionFilterProp="label"
                showSearch
                allowClear
                loading={sellers.length === 0} // Show loading indicator in select
                style={{
                  borderRadius: "6px",
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginTop: "30px" }}>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-1 border-0"
                  style={{
                    backgroundColor: "#AE0000",
                    color: "white",
                    height: "45px",
                    flex: "1",
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? "Updating..." : "Update Franchise"}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-1 border-0"
                  style={{
                    backgroundColor: "#6c757d",
                    color: "white",
                    height: "45px",
                    flex: "1",
                  }}
                  onClick={() => navigate("/franchise")}
                >
                  Cancel
                </button>
              </div>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default EditFranchise;
